import { useCallback, DragEvent, useRef } from 'react';
import {
  InternalDragData,
  InternalDragType,
  isInternalDataDrag,
  isInternalDragOfType,
  parseInternalDragData,
  setInternalDragData,
} from 'helpers/internalDragAndDrop';
import { getRowFromEvent } from 'components/common/dataGrid/dom';
import {
  DocumentationViewItem,
  isDirectoryItemType,
} from 'store/documentation/model/types';
import { Identificable } from 'shared/types/commonView';
import { debugLog } from 'shared/logger/debugLog';
import { useDocumentationStore } from 'store/documentation';
import { Color } from 'shared/utils/colors';
import { getCurrentDirectoryName } from '../model';

interface UseInternalRowDndParams {
  rows: DocumentationViewItem[];
  getRowId: (row: DocumentationViewItem) => string;
  setParent?: (parent: Identificable) => void;
  selectElements?: (
    items: DocumentationViewItem[],
    opts: { ctrlKeyPressed: boolean }
  ) => void;
  onDragStateChange: (isDragging: boolean) => void;
  onDropTargetChange: (name: string | null, id: string) => void;
}

const DRAG_PREVIEW_CONTAINER_STYLE = {
  position: 'fixed',
  top: '-9999px',
  left: '-9999px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  maxWidth: '420px',
  minWidth: '200px',
  borderRadius: '4px',
  background: '#FFFFFF',
  border: '1px solid rgba(0, 0, 0, 0.18)',
  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.16)',
  fontSize: '12px',
  fontWeight: '500',
  lineHeight: '16px',
  color: '#1F2328',
  overflow: 'visible',
} as const;

export function useInternalRowDnd({
  rows,
  getRowId,
  setParent,
  selectElements,
  onDragStateChange,
  onDropTargetChange,
}: UseInternalRowDndParams): {
  rowProps: {
    onDragStart: ((e: DragEvent<Element>) => void) | null;
    onDragOver: (e: DragEvent<Element>) => void;
    onDragEnd: (e: DragEvent<Element>) => void;
    onDrop: (e: DragEvent<Element>) => void;
  };
} {
  const getCurrentDirectory = (): string => useDocumentationStore.getState().currentDirectory;
  const getCurrentDirectoryPath = (): { _id: string; name: string }[] =>
    useDocumentationStore.getState().currentDirectoryPath;
  const getSelectedItems = (): Set<DocumentationViewItem> =>
    useDocumentationStore.getState().selectedItems;
  const dragImageRef = useRef<HTMLDivElement | null>(null);

  const removeDragImage = useCallback((): void => {
    if (!dragImageRef.current) return;
    dragImageRef.current.remove();
    dragImageRef.current = null;
  }, []);

  const setCompactDragImage = useCallback(
    (
      event: DragEvent<Element>,
      label: string,
      isDirectory: boolean,
      draggedItemsCount: number
    ): void => {
      if (!event.dataTransfer || typeof document === 'undefined') return;

      removeDragImage();

      const container = document.createElement('div');
      Object.assign(container.style, DRAG_PREVIEW_CONTAINER_STYLE);

      const icon = document.createElement('span');
      icon.style.display = 'inline-block';
      icon.style.flexShrink = '0';
      icon.style.width = '16px';
      icon.style.height = '16px';
      const rowElement = event.currentTarget as Element;
      const nameCellIcon = rowElement.querySelector(
        '.MuiDataGrid-cell[data-field="name"] svg'
      );
      if (nameCellIcon instanceof SVGElement) {
        const clonedIcon = nameCellIcon.cloneNode(true) as SVGElement;
        clonedIcon.style.width = '16px';
        clonedIcon.style.height = '16px';
        clonedIcon.style.display = 'block';
        icon.appendChild(clonedIcon);
      } else {
        icon.style.backgroundColor = '#9AA1A9';
        icon.style.borderRadius = isDirectory ? '3px' : '2px';
      }

      const text = document.createElement('span');
      text.textContent = label;
      text.style.overflow = 'hidden';
      text.style.textOverflow = 'ellipsis';
      text.style.whiteSpace = 'nowrap';
      text.style.maxWidth = '360px';

      container.appendChild(icon);
      container.appendChild(text);

      if (draggedItemsCount > 1) {
        const badge = document.createElement('span');
        badge.textContent =
          draggedItemsCount > 9 ? '9+' : String(draggedItemsCount);
        badge.style.position = 'absolute';
        badge.style.top = '-7px';
        badge.style.right = '-7px';
        badge.style.width = '18px';
        badge.style.height = '18px';
        badge.style.padding = '0';
        badge.style.borderRadius = '50%';
        badge.style.backgroundColor = Color.primaryOrange;
        badge.style.color = Color.black;
        badge.style.display = 'inline-flex';
        badge.style.alignItems = 'center';
        badge.style.justifyContent = 'center';
        badge.style.fontSize = '11px';
        badge.style.fontWeight = '600';
        badge.style.boxShadow = '0 1px 4px rgba(0,0,0,0.25)';
        container.appendChild(badge);
      }

      document.body.appendChild(container);
      event.dataTransfer.setDragImage(container, 2, 2);

      dragImageRef.current = container;
    },
    [removeDragImage]
  );

  const onRowDragStart = useCallback(
    (e: DragEvent<Element>) => {
      if (!selectElements) return;
      e.stopPropagation();

      const row = getRowFromEvent<DocumentationViewItem>(
        e,
        rows,
        getRowId
      );
      if (!row) return;

      const selectedItems = Array.from(getSelectedItems());
      const isDraggedRowInSelection = selectedItems.some(
        (item) => getRowId(item) === getRowId(row)
      );
      const draggedItems =
        isDraggedRowInSelection && selectedItems.length > 0
          ? selectedItems
          : [row];

      setInternalDragData(e, {
        type: InternalDragType.DOCUMENTATION_ITEM,
        item: row,
        items: draggedItems,
      });

      if (!isDraggedRowInSelection) {
        selectElements([row], { ctrlKeyPressed: false });
      }

      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
      }

      setCompactDragImage(
        e,
        row.name ?? '',
        isDirectoryItemType(row.type),
        draggedItems.length
      );
    },
    [rows, getRowId, selectElements, setCompactDragImage]
  );

  const onRowDragOver = useCallback(
    (e: DragEvent<Element>) => {
      if (!isInternalDataDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();

      onDragStateChange(true);
      e.dataTransfer.dropEffect = 'move';

      const row = getRowFromEvent<DocumentationViewItem>(
        e,
        rows,
        getRowId
      );

      if (row && isDirectoryItemType(row.type)) {
        const targetId = row._id ?? getCurrentDirectory();
        onDropTargetChange(row.name, targetId);
      } else {
        onDropTargetChange(
          getCurrentDirectoryName(getCurrentDirectoryPath()),
          getCurrentDirectory()
        );
      }
    },
    [rows, getRowId, onDragStateChange, onDropTargetChange]
  );

  const onRowDragEnd = useCallback(() => {
    onDragStateChange(false);
    onDropTargetChange(
      getCurrentDirectoryName(getCurrentDirectoryPath()),
      getCurrentDirectory()
    );
    removeDragImage();
  }, [onDragStateChange, onDropTargetChange, removeDragImage]);

  const onRowDrop = useCallback(
    async (e: DragEvent<Element>) => {
      if (!isInternalDataDrag(e)) return;

      onDragStateChange(false);
      onDropTargetChange(
        getCurrentDirectoryName(getCurrentDirectoryPath()),
        getCurrentDirectory()
      );

      const dragData =
        parseInternalDragData<InternalDragData<DocumentationViewItem>>(e);

      if (
        !isInternalDragOfType(
          dragData,
          InternalDragType.DOCUMENTATION_ITEM
        )
      ) {
        return;
      }

      if (!setParent || !dragData.item) return;

      e.preventDefault();
      e.stopPropagation();

      const targetRow = getRowFromEvent<DocumentationViewItem>(
        e,
        rows,
        getRowId
      );

      if (!targetRow || !isDirectoryItemType(targetRow.type)) return;

      const draggedItems = dragData.items ?? [dragData.item];
      const isDroppingOnSelf = draggedItems.some(
        (item) => item.localId === targetRow.localId
      );
      if (isDroppingOnSelf) return;

      if (!targetRow._id) {
        debugLog('Cannot move to directory without _id');
        return;
      }

      const targetIdentifiable: Identificable = {
        _id: targetRow._id,
      };

      setParent(targetIdentifiable);
      removeDragImage();
    },
    [
      rows,
      getRowId,
      setParent,
      onDragStateChange,
      onDropTargetChange,
      removeDragImage,
    ]
  );

  return {
    rowProps: {
      onDragStart: selectElements ? onRowDragStart : null,
      onDragOver: onRowDragOver,
      onDragEnd: onRowDragEnd,
      onDrop: onRowDrop,
    },
  };
}
